/**
 * Optional sign-in with Firebase (Google, Microsoft). Free plan only: email
 * link sign-in is not offered because Firebase's free plan sends 5 a day.
 *
 * The web config is public by design (it identifies the project; access is
 * enforced by Firebase and by our API verifying ID tokens). Analytics is not
 * loaded from Firebase: GA4 comes through Cloudflare.
 *
 * The Firebase SDK is imported only when someone signs in, or was signed in
 * before, so ordinary visitors never download it.
 */
import type { Auth, User } from 'firebase/auth';

// Project "scamx-11aa1" (owner, 2026-10-15). Public values: they name the
// project, they grant nothing. GATSBY_FIREBASE_* overrides them for testing.
export const firebaseConfig = {
  apiKey: process.env.GATSBY_FIREBASE_API_KEY || 'AIzaSyAMz3I54ICdzMZ1d-GbbzT2wvuSJb9MGDU',
  authDomain: process.env.GATSBY_FIREBASE_AUTH_DOMAIN || 'scamx-11aa1.firebaseapp.com',
  projectId: process.env.GATSBY_FIREBASE_PROJECT_ID || 'scamx-11aa1',
  appId: process.env.GATSBY_FIREBASE_APP_ID || '1:338728533083:web:60a0466ba23af8e5a6137e',
};

/** Microsoft needs an Azure app registered in the Firebase console first. */
export const MICROSOFT_ENABLED = process.env.GATSBY_FIREBASE_MICROSOFT === 'true';

export const authConfigured = (): boolean =>
  !!(firebaseConfig.apiKey && firebaseConfig.authDomain && firebaseConfig.projectId && firebaseConfig.appId);

/** Set while someone is signed in, so a returning visitor's session is restored without loading the SDK for everyone. */
export const SIGNED_IN_HINT = 'scamx_signed_in';

let authPromise: Promise<Auth> | null = null;

export function loadAuth(): Promise<Auth> {
  if (!authPromise) {
    authPromise = (async () => {
      const [{ initializeApp, getApps }, { getAuth, browserLocalPersistence, setPersistence }] = await Promise.all([
        import('firebase/app'),
        import('firebase/auth'),
      ]);
      const app = getApps()[0] || initializeApp(firebaseConfig);
      const auth = getAuth(app);
      await setPersistence(auth, browserLocalPersistence);
      return auth;
    })();
  }
  return authPromise;
}

export type Provider = 'google' | 'microsoft';

export async function signInWith(provider: Provider): Promise<User> {
  const auth = await loadAuth();
  const { GoogleAuthProvider, OAuthProvider, signInWithPopup } = await import('firebase/auth');
  const p = provider === 'google' ? new GoogleAuthProvider() : new OAuthProvider('microsoft.com');
  if (provider === 'google') (p as InstanceType<typeof GoogleAuthProvider>).setCustomParameters({ prompt: 'select_account' });
  const result = await signInWithPopup(auth, p);
  localStorage.setItem(SIGNED_IN_HINT, '1');
  return result.user;
}

export async function signOutUser(): Promise<void> {
  const auth = await loadAuth();
  const { signOut } = await import('firebase/auth');
  localStorage.removeItem(SIGNED_IN_HINT);
  await signOut(auth);
}
